import { useQuery } from '@tanstack/react-query';

import {
  getSingleBillCurrencyCodeByCountry,
  getSingleBillPaymentConfigurationBucket,
  getSingleBillPaymentElementConfigurationBucket,
} from '@ctct/domain-layer';
import { bpConsoleApi, stripeApi } from '@ctct/infrastructure-layer';

import { QueryKeys } from './constants';
import { usePartner } from './usePartner';
import { usePartnerOwnerSummary } from './usePartnerOwnerSummary';
import { usePartnerSummary } from './usePartnerSummary';
import { useSingleBill } from './useSingleBill';
import { useAchMicroDepositsGate } from '../experiments/useAchMicroDepositsGate';
import { PaymentStrategy, type PaymentStrategyQuery } from '../facades';
import { useQueryMemoized } from '../helpers/useQueryMemoized';

const getPartnerPuidFromQueries = (params: { partnerOwnerSummaryPuid?: number; partnerSummaryPuid?: number }) => {
  return params.partnerOwnerSummaryPuid ?? params.partnerSummaryPuid;
};

export {
  getSingleBillCurrencyCodeByCountry,
  getSingleBillPaymentConfigurationBucket,
  getSingleBillPaymentElementConfigurationBucket,
};

export const useSingleBillSetupElement = (
  paymentStrategy: PaymentStrategyQuery,
  selectedBillingCountry?: string | null,
  selectedBillingCurrency?: string | null
) => {
  const partnerQuery = usePartner();
  const partnerOwnerSummaryQuery = usePartnerOwnerSummary();
  const partnerSummaryQuery = usePartnerSummary();
  const singleBillQuery = useSingleBill();
  const { data: paymentMethodOptions, isLoading: isAchMicroDepositsGateLoading } = useAchMicroDepositsGate();

  const isStripePaymentStrategy = paymentStrategy.data === PaymentStrategy.Stripe;

  const isProvisionAccountsToStripeEnabled =
    typeof partnerQuery.data?.isPartnerProvisionAccountsToStripe === 'function' &&
    partnerQuery.data.isPartnerProvisionAccountsToStripe() === true;

  const partnerPuid = getPartnerPuidFromQueries({
    partnerOwnerSummaryPuid: partnerOwnerSummaryQuery.data?.partnerUID,
    partnerSummaryPuid: partnerSummaryQuery.data?.getPartnerUID?.(),
  });

  const countryCode = selectedBillingCountry || undefined;
  const currencyCode = selectedBillingCurrency || singleBillQuery.data?.getCurrencyCd?.() || undefined;
  const returnUrl = stripeApi.getReturnUrl();

  const paymentElementConfigurationBucket = getSingleBillPaymentElementConfigurationBucket(countryCode, currencyCode);
  const queryEnabled =
    (isStripePaymentStrategy || isProvisionAccountsToStripeEnabled) &&
    !!partnerPuid &&
    !singleBillQuery.isLoading &&
    !isAchMicroDepositsGateLoading &&
    !!countryCode &&
    !!currencyCode;

  const query = useQuery({
    queryKey: [QueryKeys.SINGLE_BILL_SETUP_ELEMENT, paymentElementConfigurationBucket, countryCode],
    queryFn: async () => {
      if (!partnerPuid) {
        throw new Error('partnerUID is required for partner setup-element');
      }

      const response = await bpConsoleApi.postSingleBillSetupElement({
        countryCode,
        currencyCode,
        returnUrl,
        paymentMethodOptions,
        // NOTE: hardcoded to `true` for now. Current myact flow only prefetches setup-element
        // and does not expose/drive `setAsDefault` from UI state yet.
        setAsDefault: true,
      });

      return response.data;
    },
    // We only want a single request attempt from the UI.
    // Retries can look like duplicate setup-element calls and may create noisy backend side effects.
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: queryEnabled,
  });

  return useQueryMemoized(query);
};

export type UseSingleBillSetupElement = ReturnType<typeof useSingleBillSetupElement>;
